import { useMemo } from "react";
import { useReadContracts } from "wagmi";
import { formatUnits, type Address } from "viem";
import {
  CURVE_ADDRESS,
  COIN_ADDRESS,
  SHIELDED_POOL_ADDRESS,
  curveAbi,
  coinAbi,
  erc20Abi,
} from "@/lib/contracts";

export type CurveStats = {
  spot?: bigint;
  reserve?: bigint;
  ratioRaw?: bigint;
  minted?: bigint;
  sMax?: bigint;
  bonded?: boolean;
  token?: Address;
  pctIssued?: number;
  perToken?: number;
  /** 01 still mintable before the curve bonds (S_MAX - minted). */
  remainingToBond?: bigint;
  /**
   * Exact USDS of buys left until the bond, read from the contract itself:
   * quoteBuy clamps any input that crosses the bond point and reports the
   * USDS it would actually use, so probing it with an amount that always
   * crosses (see BOND_PROBE_USDS) returns precisely the remaining buys.
   * Only set while the curve is still issuing.
   */
  remainingUsds?: bigint;
  /** True once issuance is at least 75% of S_MAX and the curve has not bonded. */
  nearBond?: boolean;
  /** Total 01 held in the shielded pool (the pool's 01 token balance). */
  shielded?: bigint;
  /**
   * Circulating 01 supply: the ERC-20 coin.totalSupply(). This is the real
   * supply (minted minus burned); use it for market cap, not the gross
   * `minted` counter, which still counts burned 01 (post-bond it keeps
   * falling as curve buybacks redeem and burn, but never as far as supply).
   */
  totalSupply?: bigint;
  /**
   * 01 burned in trading so far: the gap between the curve's gross bonding
   * counter (`minted`) and the token's real totalSupply(). Pre-bond this gap
   * grows by exactly the 0.25% fee burned on every trade; post-bond curve
   * buybacks also move both counters, so read it as "burned in trading at
   * the curve" rather than a pure fee tally. Real on-chain reads either way.
   */
  burned?: bigint;
  /** The hard cap on 01: coin.MAX_SUPPLY(), 21,000,000e18 on chain. */
  maxSupply?: bigint;
  /**
   * Cap headroom no one can ever mint: MAX_SUPPLY - minted. The curve is the
   * token's only minter and it closed issuance at the bond, so everything
   * above the live bonding counter is unreachable for good (the counter can
   * fall with buybacks but can never rise again). Only set once bonded;
   * before the bond this headroom could still have been issued. Note it is
   * NOT necessarily MAX_SUPPLY - S_MAX: the live counter is what matters.
   */
  neverMinted?: bigint;
  /**
   * All 01 gone from possible supply: MAX_SUPPLY - totalSupply(). This is the
   * fee burns plus the never-minted remainder, and it only grows (sells keep
   * burning, nothing can mint again). Only set once bonded, because before
   * the bond the unminted part was still issuable and counting it as gone
   * would overstate.
   */
  removed?: bigint;
  /** Force an immediate re-read (e.g. right after a trade settles). */
  refetch: () => void;
};

// A quoteBuy input guaranteed to cross the bond point at any curve state (the
// whole curve, empty to bonded, costs about 2.006M USDS in buys).
const BOND_PROBE_USDS = 3_000_000n * 10n ** 18n;

// Live curve reads shared by the persistent stat strip on every surface and by
// the dapp's own views. wagmi/react-query dedupe identical reads by key, so
// rendering the strip on home, docs, and the dapp issues one set of calls.
export function useCurveStats(): CurveStats {
  const statsRead = useReadContracts({
    contracts: [
      { address: CURVE_ADDRESS, abi: curveAbi, functionName: "spotPrice" },
      { address: CURVE_ADDRESS, abi: curveAbi, functionName: "reserveUsds" },
      { address: CURVE_ADDRESS, abi: curveAbi, functionName: "ratio" },
      { address: CURVE_ADDRESS, abi: curveAbi, functionName: "minted" },
      { address: CURVE_ADDRESS, abi: curveAbi, functionName: "S_MAX" },
      { address: CURVE_ADDRESS, abi: curveAbi, functionName: "bonded" },
      { address: COIN_ADDRESS, abi: erc20Abi, functionName: "balanceOf", args: [SHIELDED_POOL_ADDRESS] },
      { address: COIN_ADDRESS, abi: erc20Abi, functionName: "totalSupply" },
      // Bond-distance probe. quoteBuy never reverts on a buy that crosses the
      // bond point; it clamps to the remaining supply and reports the USDS it
      // would actually use in its 4th output. Total buys from an empty curve
      // to the bond are about 2.006M USDS, so a 3M probe always crosses and
      // usdsUsed is therefore the exact USDS of buys left until the bond.
      { address: CURVE_ADDRESS, abi: curveAbi, functionName: "quoteBuy", args: [BOND_PROBE_USDS] },
      { address: COIN_ADDRESS, abi: coinAbi, functionName: "MAX_SUPPLY" },
    ],
    query: { refetchInterval: 12_000 },
  });

  const refetch = statsRead.refetch;
  return useMemo(() => {
    const d = statsRead.data;
    const big = (i: number) =>
      d && d[i]?.status === "success" ? (d[i].result as bigint) : undefined;
    const reserve = big(1);
    const minted = big(3);
    const sMax = big(4);
    const bonded = d && d[5]?.status === "success" ? (d[5].result as boolean) : undefined;
    const token = COIN_ADDRESS;
    const pctIssued =
      minted !== undefined && sMax !== undefined && sMax > 0n
        ? Number((minted * 10000n) / sMax) / 100
        : undefined;
    const totalSupply = big(7);
    // Backing per token divides by the real circulating supply. Post-bond that
    // is coin.totalSupply(): the gross `minted` counter freezes at S_MAX and
    // still counts burned 01, which would understate the backing. Pre-bond the
    // project's circulating convention is the minted counter.
    const perTokenDenom = bonded === true ? totalSupply : minted;
    const perToken =
      reserve !== undefined && perTokenDenom !== undefined && perTokenDenom > 0n
        ? Number(formatUnits(reserve, 18)) / Number(formatUnits(perTokenDenom, 18))
        : undefined;
    const burned =
      minted !== undefined && totalSupply !== undefined && minted >= totalSupply
        ? minted - totalSupply
        : undefined;
    const maxSupply = big(9);
    // Both figures are only meaningful once the bond has closed issuance for
    // good; before that the unminted remainder could still be issued.
    const neverMinted =
      bonded === true && maxSupply !== undefined && minted !== undefined && maxSupply >= minted
        ? maxSupply - minted
        : undefined;
    const removed =
      bonded === true &&
      maxSupply !== undefined &&
      totalSupply !== undefined &&
      maxSupply >= totalSupply
        ? maxSupply - totalSupply
        : undefined;
    const remainingToBond =
      minted !== undefined && sMax !== undefined && sMax >= minted ? sMax - minted : undefined;
    const bondQuote =
      d && d[8]?.status === "success" ? (d[8].result as readonly bigint[]) : undefined;
    const remainingUsds = bonded === false && bondQuote ? bondQuote[3] : undefined;
    const nearBond = bonded === false && pctIssued !== undefined && pctIssued >= 75;
    return {
      spot: big(0),
      reserve,
      ratioRaw: big(2),
      minted,
      sMax,
      bonded,
      token,
      pctIssued,
      perToken,
      remainingToBond,
      remainingUsds,
      nearBond,
      shielded: big(6),
      totalSupply,
      burned,
      maxSupply,
      neverMinted,
      removed,
      refetch: () => {
        void refetch();
      },
    };
  }, [statsRead.data, refetch]);
}
